/* eslint-disable import/prefer-default-export */
import { defineStore } from "pinia";

import { useGlobalStore } from "@/stores/globalStore";
import { useSellOrderFormStore } from "@/stores/forms/sellOrderFormStore";
import { useBuyOrderFormStore } from "@/stores/forms/buyOrderFormStore";
import { useNFTOrderFormStore } from "@/stores/forms/nftOrderFormStore";

import usePaymentFilters from "@/composables/filters/usePaymentFilters";
import useLocalStorage from "@/composables/storage/useLocalStorage.js";
import useBlockchainFilters from "@/composables/filters/useBlockchainFilters";

const { checkLocalStorage, bindLocalStorage } = useLocalStorage();
const { getActiveCoinData } = useBlockchainFilters();
const {
  filterPaymentProvider,
  filterPaymentDataByPaymentName,
  filterPaymentsByFiatAndCoin,
} = usePaymentFilters();

const settings = {
  mode: "BUY", // to come from merch settings
  defaultCoinType: "BTC",
  defaultBLockchain: "BTC",
  defaultFiatType: "USD",
  defaultFiatAmount: 100, 
};

export const useOrderFormStore = defineStore({
  id: "order",
  state: () => ({
    settings,
    form: {
      ...{
        mode: checkLocalStorage("mode", settings.mode),
      },
    },
    orderAccordionData: checkLocalStorage("orderAccordion", {
      fiatAmount: 100,
      fiatType: settings.defaultFiatType,
      coinType: settings.defaultCoinType,
      coinAmount: 0,
      networkFee: 0,
      gatewayFee: 0,
      receivedRate: 0,
      quoteValue: 0,
    }),
    merchApiOrderData: {},
  }),
  actions: {
    updateDefaultCoin(coinType) {
      if (coinType){
        this.settings.defaultCoinType = coinType;
      }
    },
    updateOrderAccordion(key, value) {
      this.orderAccordionData[key] = value;
      bindLocalStorage("orderAccordion", this.orderAccordionData);
    },
    // currently modes can be BUY, SELL or NFT-BUY
    setActiveMode(mode) {
      this.form.mode = mode;
      bindLocalStorage("mode", this.form.mode);
    },
    setActiveModePaymentProvider(provider) {
      this.getActiveStore.setPaymentServiceProvider(provider);
    },
    setPspTypeCode(pspCode) {
      // Working of this function can be changed to include other parameters of the Payment
      this.getActiveStore.setPspTypeCode(pspCode);
    },
    setPspRequireMobile(value) {
      this.getActiveStore.form.payment.require_mobile = value;
    },
    setPaymentServiceProvider(value) {
      this.getActiveStore.setPaymentServiceProvider(value);
    },
    toggleBuySellMode() {
      const switchMode = this.form.mode === "BUY" ? "SELL" : "BUY";
      this.setActiveMode(switchMode);
    },
    setMerchApiData(data) {
      this.merchApiOrderData = data;
    },
  },
  getters: {
    isBuyMode() {
      return this.getActiveMode === "BUY";
    },
    isSellMode() {
      return this.getActiveMode === "SELL";
    },
    isNFTMode() {
      return this.getActiveMode === "NFT-BUY";
    },
    getActiveStore() {
      const activeMode = this.getActiveMode;
      const buyStore = useBuyOrderFormStore();
      const sellStore = useSellOrderFormStore();
      const nftStore = useNFTOrderFormStore();

      switch (activeMode) {
        case "BUY":
          return buyStore;
        case "SELL":
          return sellStore;
        case "NFT-BUY":
          return nftStore;
        default:
          return buyStore;
      }
    },

    getActiveStorePaymentData() {
      const globalStore = useGlobalStore();
      const paymentData = this.isBuyMode
        ? globalStore.global.payments
        : this.isSellMode
        ? globalStore.global.payouts
        : globalStore.global.nftPayments;

      return paymentData;
    },
    getActiveMode() {
      return this.form.mode;
    },
    getFiatType() {
      if (!this.getActiveStore.form?.fiatType) {
        return null;
      }
      return this.getActiveStore.form.fiatType.value;
    },
    getCoinType() {
      if (!this.getActiveStore.form?.coinType) {
        return null;
      }
      return this.getActiveStore.form.coinType.value;
    },
    getOrderType(store) {
      if (!store.orderAccordionData?.coinType) {
        return null;
      }
      return store.orderAccordionData.orderType;
    },

    getRestrictedCountriesByActiveCoin() {
      const { coinType, data } = this.getActiveStore.coinData;
      const coinData = getActiveCoinData(data, coinType);
      if (!coinData) return [];
      return coinData?.restrictions?.country;
    },

    getActivePaymentByMode() {
      return this.getActiveStore.form.payment.value;
    },
    getPaymentProvider() {
      return filterPaymentProvider(
        this.getActiveStorePaymentData,
        this.getActivePaymentByMode
      );
    },

    filterPaymentDataByPaymentName() {
      return filterPaymentDataByPaymentName(
        this.getActiveStorePaymentData,
        this.getActivePaymentByMode
      );
    },

    filterPaymentsByFiatAndCoin() {
      const activeFiat = this.getActiveStore.form.fiatType.value;
      const activeCoin = this.getActiveStore.form.coinType.value;

      return filterPaymentsByFiatAndCoin(
        activeFiat,
        activeCoin,
        this.getActiveStorePaymentData
      );
    },

    getPaymentObject() {
      if (!this.getActiveStore.form?.payment) {
        return null;
      }
      return this.getActiveStore.form.payment;
    },

    isOrderFromMerchantApi() {
      return !!Object.getOwnPropertyDescriptor(
        this.merchApiOrderData,
        "orderId"
      );
    },

    getMerchantApiData() {
      return this.merchApiOrderData;
    },

    canSkipOrderPageFromApiData() {
      return (
        this.merchApiOrderData?.redirectTo &&
        this.merchApiOrderData.redirectTo !== "order"
      );
    },
  },
});
